import ShopUtils from './Shop_Utils';
import OMT_UI_SquareButton, { BUTTONCOLOURS } from '../../../OMT_UI/OMT_UI_SquareButton';
import OMT_UI_DrawUtil from '../../../OMT_UI/Drawing/OMT_UI_DrawUtil';

const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 65;
const BORDERSIZE = 2;
const DEFAULT_SLICE = {
  left: 27,
  right: 32,
  top: 25,
  bottom: 25,
};
const ICON_PADDING = 60;
// const MOST_POPULAR = 'Most Popular';
const BEST_DEAL = 'Best Deal';

export default class ShopEntry extends Phaser.Group {
  /**
   * The individual packages for the shop. Does not have the big special banner thing
   * @param {Phaser.Game} game
   * @param {Object} config
   * @param {string} config.icon Icon asset string. The icon appears on the left
   * @param {string} config.item Item details for when shopping
   * @param {Phaser.Rectangle} config.dimensions Dimensions for entry. Assumes default if nothing significant is given. Allows flexibility
   * @param {string} config.buttonType Button asset/color string
   * @param {string} config.buttonTextStyle Button text style
   * @param {function} config.buttonCallBack Button callback when clicked
   */
  constructor(game, config) {
    super(game, null);

    this._config = this._mergeConfig(config);

    this._drawEverything();
  }

  /**
   * Destroy!
   */
  destroy() {
    this.button.onClick.dispose(); // Properly remove signal
    super.destroy();
  }

  /**
   * Merges given config with defaults
   * @param {Object} inConfig
   */
  _mergeConfig(inConfig) {
    const mergedConfig = {
      icon: (inConfig && inConfig.icon) ? inConfig.icon : 'shop_deal_1', // Defaults to package 1
      item: inConfig.item, // Untouched item. It gets sent to Facebook to do the purchasing
      dimensions: {
        x: (inConfig && inConfig.dimensions && inConfig.dimensions.x) ? inConfig.dimensions.x : 0, // Custom x
        y: (inConfig && inConfig.dimensions && inConfig.dimensions.y) ? inConfig.dimensions.y : 0, // Custom y
        width: (inConfig && inConfig.dimensions && inConfig.dimensions.width) ? inConfig.dimensions.width : DEFAULT_WIDTH, // Custom width
        height: (inConfig && inConfig.dimensions && inConfig.dimensions.height) ? inConfig.dimensions.height : DEFAULT_HEIGHT, // Custom height
      },
      buttonType: (inConfig && inConfig.buttonType) ? inConfig.buttonType : 'lightGreen', // Defaults to the green button
      buttonTextStyle: (inConfig && inConfig.buttonTextStyle) ? inConfig.buttonTextStyle : 'shop2-Buy', // Default textstyle
      buttonCallBack: (inConfig && inConfig.buttonCallBack) ? inConfig.buttonCallBack : null, // Assume nothing if nothing is given
      iconAnimation: (inConfig && inConfig.iconAnimation) ? inConfig.iconAnimation : null, // pass in a function to animate the icon
    };
    // View name is what the user sees as the package name
    mergedConfig.viewName = (inConfig && inConfig.viewName) ? inConfig.viewName : mergedConfig.item.title.replace(' Coins', '');
    mergedConfig.viewName = OMT.language.toLocaleNumber(mergedConfig.viewName);

    return mergedConfig;
  }

  /**
   * Purchases things or does the callback
   */
  _onButtonClick() {
    if (!game) { return; }
    if (this._config.buttonCallBack === null) {
      ShopUtils.purchaseItem(this._config.item, this);
    } else {
      this._config.buttonCallBack();
    }
  }

  /**
   * Draws all the visuals and layouts them together.
   */
  _drawEverything() {
    this.container = new Phaser.Group(game, this);
    const { container } = this;

    // Background
    const background = new PhaserNineSlice.NineSlice(0, 0, 'shop2', 'number_bg', this._config.dimensions.width, this._config.dimensions.height, DEFAULT_SLICE);
    background.anchor.set(0.5);
    container.addChild(background);

    // The calculated height for each entry is based on the background. For external use for layout-ing
    this.calculatedHeight = background.height;
    this.calculatedWidth = background.width;

    if (this._config.item.description !== '') { // Does this have a tag?
      const tagContainer = new Phaser.Group(game, container);
      const isBestDeal = this._config.item.description.toLowerCase() === BEST_DEAL.toLowerCase(); // Is it the best deal?

      // Make the border
      const border = this._makeTagBorder(isBestDeal);

      // Make the tag
      const targetTag = isBestDeal ? 'best_value_button' : 'best_popular_button';
      const tag = G.makeImage(0, 0, targetTag, 0.5, null);
      tag.y = border.y - (border.height + tag.height) / 2;

      // Make the tag text
      const tagText = new G.Text(
        0,
        4,
        OMT.language.getText(this._config.item.description),
        isBestDeal ? 'shop2-BestValue' : 'shop2-MostPopular',
        0.5,
        tag.width,
        tag.height * 1.5,
        true,
        'center',
      );

      // Assemble
      tag.addChild(tagText);
      tagContainer.addChild(tag);
      tagContainer.addChild(border);
    }

    // The Icon that is shown on the left
    const icon = G.makeImage(0, 0, this._config.icon, 0.5, container);
    const iconScale = (background.height * 1.5) / icon.height; // Icon is 1.5 * the background height
    icon.scale.set(iconScale);
    icon.x = background.x + ICON_PADDING - background.width / 2; // Positioned a set distance from the left, to keep everything aligned
    if (this._config.iconAnimation) {
      this._config.iconAnimation(icon);
    }

    // The button
    const buttonOffsetY = G.OMTsettings.elements.Window_shop2.entryButtonYOffset || 0;
    const button = new G.Button(0, 0, null, this._onButtonClick.bind(this)); // The button
    this.button = button;
    const buttonContainer = new Phaser.Group(game, button); // The container to scale the button
    const buttonImage = new OMT_UI_SquareButton(0, 0, { // Without the nineslice affecting it
      button: {
        tint: BUTTONCOLOURS[this._config.buttonType],
        dimensions: {
          width: 196, // original button size
          height: 100,
        },
        extraDetail: false,
      },
      options: {
        clickFunction: {
          scaleOnClick: false,
        },
      },
    });
    buttonImage.inputEnabled = false; // So it won't eat inputs. BUTTONSTATE.disabled will automatically set the button to grey
    buttonContainer.addChild(buttonImage);
    const buttonScale = (background.height * 0.7) / buttonImage.height; // Find the size it needs to be
    buttonContainer.scale.set(buttonScale);
    const buttonModDimensions = { width: (buttonImage.width * buttonScale), height: (buttonImage.height * buttonScale) }; // I don't want to use long version
    const buttonText = new G.Text(0, 1, this._config.item.price, this._config.buttonTextStyle, 0.5, buttonModDimensions.width * 0.8, buttonModDimensions.height * 0.8, true, 'center');
    button.addChild(buttonText);
    button.x = Math.round(background.x + (background.width / 2) - (buttonModDimensions.width * 0.6));
    button.y = Math.round(buttonOffsetY);
    container.addChild(button);

    // Calculating the available width for the shop entry text
    const availableWidth = (button.x - (buttonImage.width / 2)) - (icon.x + (icon.width / 2));
    const coinReward = new G.Text(
      0,
      2,
      this._config.viewName,
      'shop2-Entry',
      0.5,
      availableWidth,
      background.height * 0.8,
      true,
      'center',
    );
    container.addChild(coinReward);

    // Caching
    background.cacheAsBitmap = true;
    icon.cacheAsBitmap = true;
    coinReward.cacheAsBitmap = true;
  }

  _makeTagBorder(isBestDeal) {
    let border; // For return
    const targetBorder = isBestDeal ? 'shop2BestValue' : 'shop2MostPopular'; // Find target asset
    const targetDimensions = { width: this._config.dimensions.width + BORDERSIZE, height: this._config.dimensions.height + BORDERSIZE }; // Get the target dimensions

    if (G.OMTsettings.elements.Window_shop2.useTileEntryBorder === true) { // Are we using tile assets?
      border = OMT_UI_DrawUtil.drawNineSliceTileBox({
        spreadsheetName: 'shop2',
        assetName: targetBorder,
        sliceDimensions: DEFAULT_SLICE,
        bodyWidth: targetDimensions.width,
      });
    } else {
      border = new PhaserNineSlice.NineSlice(0, 0, 'shop2', targetBorder, targetDimensions.width, targetDimensions.height, DEFAULT_SLICE);
      border.anchor.set(0.5);
    }

    return border;
  }
}
